// Employee/Views/MonthView.tsx
import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {styles} from "../styles/MonthViewStyles";
import {Event} from "../types";
import {
    addDays,
    eachDayOfInterval,
    endOfMonth,
    format,
    isSameMonth,
    parseISO,
    startOfMonth,
    startOfWeek,
} from "date-fns";
import {useTheme} from "../../Theme/ThemeContext";
import {useProfile} from "../../User/ProfileContext";
import {ru} from "date-fns/locale";
import {useNavigate} from "react-router-dom";

interface MonthViewProps {
    events: Event[];
    currentMonthStart: Date;
    setEvents: React.Dispatch<React.SetStateAction<Event[]>>;
    handleEventEdit: (event: Event | null) => void;
    canEdit: boolean;
    userType: "client" | "psychologist" | "observer";
    showButtons: boolean;
}

const MonthView: React.FC<MonthViewProps> = ({
                                                 events,
                                                 currentMonthStart,
                                                 handleEventEdit,
                                                 canEdit,
                                                 userType,
                                                 showButtons,
                                             }) => {
    const {plt} = useTheme();
    const {selectedProfile} = useProfile();
    const navigate = useNavigate();
    // State for managing popup
    const [selectedDayEvents, setSelectedDayEvents] = useState<Event[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const showButton = userType !== "observer" && showButtons;
    const startOfCalendar = startOfWeek(startOfMonth(currentMonthStart), {
        weekStartsOn: 1,
    });
    const endOfCalendar = addDays(
        startOfWeek(endOfMonth(currentMonthStart), {weekStartsOn: 1}),
        6
    );

    const daysInCalendar = eachDayOfInterval({
        start: startOfCalendar,
        end: endOfCalendar,
    });

    const tableRef = useRef<HTMLTableElement>(null);

    useEffect(() => {
        const resizeTable = () => {
            if (tableRef.current) {
                const windowHeight = window.innerHeight;
                const tableTop = tableRef.current.getBoundingClientRect().top;
                const newHeight = windowHeight - tableTop - 20;
                tableRef.current.style.height = `${newHeight}px`;
            }
        };

        resizeTable();
        window.addEventListener("resize", resizeTable);

        return () => {
            window.removeEventListener("resize", resizeTable);
        };
    }, []);

    const getEventsForDay = (day: Date) => {
        return events.filter(
            (event) =>
                format(parseISO(event.start), "yyyy-MM-dd") ===
                format(day, "yyyy-MM-dd")
        );
    };

    const handleDayClick = (day: Date) => {
        const dayEvents = getEventsForDay(day);
        if (dayEvents.length > 1) {
            setSelectedDayEvents(dayEvents);
            setIsDialogOpen(true);
        }
    };

    const handleEventClick = (
        event: Event,
        e: React.MouseEvent | React.TouchEvent
    ) => {
        e.stopPropagation();

        if (event.status !== "AVAILABLE") {
            navigate(`/consultations/${event.id}/`);
            return;
        }

        if (
            (userType === "psychologist" && canEdit) ||
            (userType === "client" && event.status === "AVAILABLE")
        ) {
            handleEventEdit(event);
        }
    };

    const renderEventTitle = (event: Event) => {
        if (selectedProfile === "client" && event.psychologists?.[0]) {
            const psych = event.psychologists[0];
            return `Dr. ${psych.first_name} ${psych.last_name}`;
        } else if (selectedProfile === "psychologist" && event.clients?.[0]) {
            const client = event.clients[0];
            return `${client.first_name} ${client.last_name}`;
        }
        return event.title || "Unnamed Event";
    };

    const getEventWordForm = (count: number) => {
        // Handle numbers ending in 11-14 specially
        if (count % 100 >= 11 && count % 100 <= 14) {
            return "событий";
        }

        // Handle other cases based on last digit
        switch (count % 10) {
            case 1:
                return "событие";
            case 2:
            case 3:
            case 4:
                return "события";
            default:
                return "событий";
        }
    };

    const renderDayEvents = (dayEvents: Event[], day: Date) => {
        if (dayEvents.length === 0) return null;

        if (dayEvents.length === 1) {
            const event = dayEvents[0];
            return (
                <Box
                    key={event.id}
                    sx={{
                        ...styles.eventCard,
                        backgroundColor: plt.mode === "dark" ? "#1e1e1e" : "#444",
                        cursor:
                            showButton &&
                            ((userType === "psychologist" && canEdit) ||
                                (userType === "client" && event.status === "AVAILABLE"))
                                ? "pointer"
                                : "default",
                    }}
                    onClick={(e) => handleEventClick(event, e)}
                    onTouchEnd={(e) => handleEventClick(event, e)}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: plt.mode === "dark" ? "white" : "white",
                            ...styles.eventTitle,
                        }}
                    >
                        {renderEventTitle(event)}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: plt.mode === "dark" ? "white" : "white",
                            ...styles.eventTime,
                        }}
                    >
                        {format(parseISO(event.start), "HH:mm")} -{" "}
                        {format(parseISO(event.end), "HH:mm")}
                    </Typography>
                </Box>
            );
        }

        // Multiple events - show single card with count
        return (
            <Box
                sx={{
                    ...styles.eventCard,
                    backgroundColor: plt.mode === "dark" ? "#1e1e1e" : "#444",
                    cursor: "pointer",
                }}
                onClick={() => {
                    setSelectedDayEvents(dayEvents);
                    setIsDialogOpen(true);
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: plt.mode === "dark" ? "white" : "white",
                        ...styles.eventTitle,
                    }}
                >
                    {dayEvents.length} {getEventWordForm(dayEvents.length)}
                </Typography>
            </Box>
        );
    };

    return (
        <>
            <Box sx={styles.wrapper}>
                <Box sx={styles.tableWrapper}>
                    <Table stickyHeader sx={styles.table}>
                        <TableHead>
                            <TableRow sx={{borderBottom: "none"}}>
                                {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map(
                                    (day, index) => (
                                        <TableCell
                                            key={index}
                                            sx={{
                                                ...styles.headerCell,
                                                borderBottom: "none",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color:
                                                        plt.mode === "dark" ? "white" : "black",
                                                }}
                                            >
                                                {day}
                                            </Typography>
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {Array.from({length: Math.ceil(daysInCalendar.length / 7)}).map(
                                (_, weekIndex) => (
                                    <TableRow
                                        key={weekIndex}
                                        sx={{
                                            ...styles.tableRow,
                                            borderTop: weekIndex === 0 ? "1px solid #444" : "none",
                                        }}
                                    >
                                        {daysInCalendar
                                            .slice(weekIndex * 7, weekIndex * 7 + 7)
                                            .map((day, dayIndex) => {
                                                const dayEvents = getEventsForDay(day);
                                                return (
                                                    <TableCell
                                                        key={dayIndex}
                                                        sx={{
                                                            ...styles.dayColumn,
                                                            borderBottom: "1px solid #444",
                                                            borderRight: "1px solid #444",
                                                            borderLeft:
                                                                dayIndex === 0 ? "1px solid #444" : "none",
                                                            backgroundColor: isSameMonth(
                                                                day,
                                                                currentMonthStart
                                                            )
                                                                ? plt.mode === "dark"
                                                                    ? "transparent"
                                                                    : "transparent"
                                                                : plt.mode === "dark"
                                                                    ? "#1e1e1e"
                                                                    : styles.dayColumn["&.not-current-month"]
                                                                        .backgroundColor,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                ...styles.dateNumber,
                                                                color: isSameMonth(day, currentMonthStart)
                                                                    ? plt.mode === "dark"
                                                                        ? "white"
                                                                        : "black"
                                                                    : plt.mode === "dark"
                                                                        ? "#999"
                                                                        : styles.dayColumn["&.not-current-month"]
                                                                            .color,
                                                            }}
                                                        >
                                                            {format(day, "d")}
                                                        </Typography>
                                                        <Box sx={styles.eventsContainer}>
                                                            {renderDayEvents(dayEvents, day)}
                                                        </Box>
                                                    </TableCell>
                                                );
                                            })}
                                    </TableRow>
                                )
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Box>

            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
                <DialogTitle>
                    События на{" "}
                    {format(selectedDayEvents[0]?.start || new Date(), "d MMMM", {
                        locale: ru,
                    })}
                </DialogTitle>
                <DialogContent className="max-h-[80vh] overflow-y-auto">
                    <div className="space-y-4">
                        {selectedDayEvents.map((event) => (
                            <div
                                key={event.id}
                                className={`
            p-4 rounded-lg 
            ${plt.mode === "dark" ? "bg-[#2a2a2a]" : "bg-[#f0f0f0]"}
            ${
                                    event.status !== "AVAILABLE" ||
                                    (userType === "psychologist" && canEdit) ||
                                    (userType === "client" && event.status === "AVAILABLE")
                                        ? "cursor-pointer hover:bg-opacity-80"
                                        : ""
                                }
          `}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (event.status !== "AVAILABLE") {
                                        navigate(`/consultations/${event.id}/`);
                                        setIsDialogOpen(false);
                                        return;
                                    }

                                    if (
                                        (userType === "psychologist" && canEdit) ||
                                        (userType === "client" && event.status === "AVAILABLE")
                                    ) {
                                        handleEventEdit(event);
                                        setIsDialogOpen(false);
                                    }
                                }}
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <Typography
                                        variant="body1"
                                        className={`font-bold ${
                                            plt.mode === "dark"
                                                ? "text-white"
                                                : "text-black"
                                        }`}
                                    >
                                        {renderEventTitle(event)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        className={`${
                                            plt.mode === "dark"
                                                ? "text-gray-300"
                                                : "text-gray-600"
                                        }`}
                                    >
                                        {format(parseISO(event.start), "HH:mm")} -{" "}
                                        {format(parseISO(event.end), "HH:mm")}
                                    </Typography>
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthView;
